import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Search, Plus, ChevronRight, Shield, Phone, Car, Cloud, Zap, Printer, File,
  Filter, Clock, ArrowUpDown, Landmark, CreditCard, Wrench, Building2, Briefcase, Newspaper,
  Trash2, X
} from "lucide-react";
import BulkLinkVehiclesDialog from "../../components/engagements/BulkLinkVehiclesDialog";
import BulkDeleteContractsDialog from "../../components/engagements/BulkDeleteContractsDialog";

const API = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping with all categories
const categoryIcons = {
  pret: { icon: Landmark, color: "text-emerald-600", bg: "bg-emerald-100", label: "Prêt bancaire" },
  credit: { icon: CreditCard, color: "text-teal-600", bg: "bg-teal-100", label: "Crédit" },
  assurance: { icon: Shield, color: "text-blue-600", bg: "bg-blue-100", label: "Assurance" },
  telephonie: { icon: Phone, color: "text-purple-600", bg: "bg-purple-100", label: "Téléphonie" },
  leasing: { icon: Car, color: "text-red-600", bg: "bg-red-100", label: "Leasing" },
  saas: { icon: Cloud, color: "text-cyan-600", bg: "bg-cyan-100", label: "SaaS" },
  energie: { icon: Zap, color: "text-amber-600", bg: "bg-amber-100", label: "Énergie" },
  materiel: { icon: Printer, color: "text-gray-600", bg: "bg-gray-200", label: "Matériel" },
  maintenance: { icon: Wrench, color: "text-orange-600", bg: "bg-orange-100", label: "Maintenance" },
  location: { icon: Building2, color: "text-indigo-600", bg: "bg-indigo-100", label: "Location" },
  services: { icon: Briefcase, color: "text-pink-600", bg: "bg-pink-100", label: "Services" },
  abonnement: { icon: Newspaper, color: "text-violet-600", bg: "bg-violet-100", label: "Abonnement" },
  autre: { icon: File, color: "text-slate-600", bg: "bg-slate-200", label: "Autre" }
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount) return "0 €";
  return new Intl.NumberFormat("fr-FR", { 
    style: "currency", 
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Status badge
const StatusBadge = ({ statut, urgence, en_reconduction, reconduction_tacite }) => {
  // Priorité: reconduction active en premier
  if (en_reconduction) {
    return (
      <Badge className="bg-purple-100 text-purple-800 border-purple-200" title="Contrat en tacite reconduction">
        🔁 En reconduction
      </Badge>
    );
  }
  if (statut === "resilier") {
    return <Badge className="bg-gray-100 text-gray-600 border-gray-200">Résilié</Badge>;
  }
  if (statut === "resiliation_en_cours") {
    return <Badge className="bg-orange-500 text-white border-orange-600">Résiliation en cours</Badge>;
  }
  if (statut === "expire") {
    return <Badge className="bg-red-100 text-red-700 border-red-200">Expiré</Badge>;
  }
  
  const urgenceStyles = {
    critique: "bg-red-100 text-red-700 border-red-200",
    urgent: "bg-orange-100 text-orange-700 border-orange-200",
    attention: "bg-yellow-100 text-yellow-700 border-yellow-200",
    ok: "bg-green-100 text-green-700 border-green-200"
  };
  
  const labels = {
    critique: "Urgent",
    urgent: "Attention",
    attention: "À surveiller",
    ok: "Actif"
  };
  
  return (
    <span className="inline-flex items-center gap-1">
      <Badge className={urgenceStyles[urgence] || urgenceStyles.ok}>
        {labels[urgence] || "Actif"}
      </Badge>
      {reconduction_tacite === true && (
        <span
          className="text-xs px-1 py-0.5 bg-purple-50 text-purple-700 rounded border border-purple-200"
          title="Tacite reconduction active — attention au préavis"
        >
          🔁
        </span>
      )}
    </span>
  );
};

export default function ContratsList() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [bulkLinkOpen, setBulkLinkOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  
  // Filters
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");

  useEffect(() => {
    fetchContracts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [contracts, search, categoryFilter, statusFilter, sortBy]);

  const fetchContracts = async () => {
    try {
      const res = await axios.get(`${API}/api/engagements/contrats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContracts(res.data.contracts || []);
    } catch (error) {
      console.error("Error fetching contracts:", error);
      toast.error("Erreur de chargement des contrats");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...contracts];
    
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(c => 
        c.nom_contrat?.toLowerCase().includes(searchLower) ||
        c.prestataire?.toLowerCase().includes(searchLower)
      );
    }
    
    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(c => c.categorie === categoryFilter);
    }
    
    // Status filter
    if (statusFilter !== "all") {
      if (statusFilter === "actif") {
        filtered = filtered.filter(c => c.statut === "actif" && c.urgence === "ok");
      } else if (statusFilter === "urgent") {
        filtered = filtered.filter(c => c.statut === "actif" && ["critique", "urgent", "attention"].includes(c.urgence));
      } else if (statusFilter === "resiliation_en_cours") {
        filtered = filtered.filter(c => c.statut === "resiliation_en_cours");
      } else if (statusFilter === "resilier") {
        filtered = filtered.filter(c => c.statut === "resilier");
      } else {
        filtered = filtered.filter(c => c.statut === statusFilter);
      }
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "montant":
          return (b.montant_mensuel_ht || 0) - (a.montant_mensuel_ht || 0);
        case "echeance":
          return new Date(a.date_echeance || "9999-12-31") - new Date(b.date_echeance || "9999-12-31");
        case "preavis":
          return (a.jours_avant_preavis || 999) - (b.jours_avant_preavis || 999);
        case "nom":
          return (a.prestataire || "").localeCompare(b.prestataire || "");
        default: // date
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    
    setFilteredContracts(filtered);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">Tous les contrats</h1>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="contrats-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tous les contrats</h1>
          <p className="text-slate-500 mt-1">{filteredContracts.length} contrat{filteredContracts.length > 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={selectionMode ? "default" : "outline"}
            onClick={() => {
              setSelectionMode((v) => !v);
              setSelectedIds(new Set());
            }}
            data-testid="toggle-selection-mode"
          >
            {selectionMode ? <X className="w-4 h-4 mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            {selectionMode ? "Quitter la sélection" : "Sélection multiple"}
          </Button>
          <Button variant="outline" onClick={() => setBulkLinkOpen(true)} data-testid="bulk-link-btn">
            <Car className="w-4 h-4 mr-2" />
            Lier au Parc Auto
          </Button>
          <Button onClick={() => navigate("/engagements/import")}>
            <Plus className="w-4 h-4 mr-2" />
            Importer un contrat
          </Button>
        </div>
      </div>

      {/* Barre d'action sélection */}
      {selectionMode && selectedIds.size > 0 && (
        <div className="sticky top-2 z-10 bg-slate-900 text-white rounded-lg px-4 py-3 flex items-center justify-between shadow-lg">
          <div className="text-sm">
            <span className="font-semibold">{selectedIds.size}</span> contrat{selectedIds.size > 1 ? "s" : ""} sélectionné{selectedIds.size > 1 ? "s" : ""}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-slate-800"
              onClick={() => setSelectedIds(new Set())}
            >
              Tout désélectionner
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setBulkDeleteOpen(true)}
              data-testid="open-bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer la sélection
            </Button>
          </div>
        </div>
      )}

      <BulkLinkVehiclesDialog
        open={bulkLinkOpen}
        onOpenChange={setBulkLinkOpen}
        token={token}
        onSuccess={() => { fetchContracts?.(); }}
      />

      <BulkDeleteContractsDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        contractIds={[...selectedIds]}
        token={token}
        onSuccess={() => {
          setSelectedIds(new Set());
          setSelectionMode(false);
          fetchContracts();
        }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher un contrat..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[160px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {Object.entries(categoryIcons).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="actif">Actif</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="resiliation_en_cours">Résiliation en cours</SelectItem>
                <SelectItem value="resilier">Résilié</SelectItem>
                <SelectItem value="expire">Expiré</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[160px]">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Trier par" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Date d'ajout</SelectItem>
                <SelectItem value="montant">Montant</SelectItem>
                <SelectItem value="echeance">Échéance</SelectItem>
                <SelectItem value="preavis">Préavis</SelectItem>
                <SelectItem value="nom">Nom</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contracts List */}
      {filteredContracts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <File className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Aucun contrat trouvé</p>
            {contracts.length === 0 && (
              <Button variant="outline" className="mt-4" onClick={() => navigate("/engagements/import")}>
                <Plus className="w-4 h-4 mr-2" />
                Importer votre premier contrat
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredContracts.map((contrat) => {
            const cat = categoryIcons[contrat.categorie] || categoryIcons.autre;
            const Icon = cat.icon;
            
            return (
              <Card 
                key={contrat.id}
                className={`hover:shadow-md transition-shadow cursor-pointer ${
                  selectedIds.has(contrat.id) ? "ring-2 ring-indigo-500 bg-indigo-50/40" : ""
                }`}
                onClick={() => {
                  if (selectionMode) {
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(contrat.id)) next.delete(contrat.id);
                      else next.add(contrat.id);
                      return next;
                    });
                  } else {
                    navigate(`/engagements/contrats/${contrat.id}`);
                  }
                }}
                data-testid={`contract-card-${contrat.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {selectionMode && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(contrat.id)}
                          onChange={() => {}} // click bubble to card
                          onClick={(e) => e.stopPropagation() /* handled by card click above */}
                          className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                          data-testid={`select-contract-${contrat.id}`}
                        />
                      )}
                      <div className={`w-12 h-12 rounded-lg ${cat.bg} flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${cat.color}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{contrat.prestataire}</p>
                          <span className="text-slate-400">—</span>
                          <p className="text-slate-700">{contrat.nom_contrat}</p>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                          <span>{cat.label}</span>
                          {contrat.date_echeance && (
                            <>
                              <span>•</span>
                              <span>Échéance : {new Date(contrat.date_echeance).toLocaleDateString("fr-FR")}</span>
                            </>
                          )}
                          {contrat.jours_avant_preavis != null && contrat.jours_avant_preavis >= 0 && (
                            <>
                              <span>•</span>
                              <span className={contrat.jours_avant_preavis <= 30 ? "text-orange-600 font-medium" : ""}>
                                <Clock className="w-3 h-3 inline mr-1" />
                                Préavis dans {contrat.jours_avant_preavis}j
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="font-bold text-lg text-slate-900">
                          {formatCurrency(contrat.montant_mensuel_ht)}
                        </p>
                        <p className="text-xs text-slate-400">/mois</p>
                      </div>
                      <StatusBadge
                        statut={contrat.statut}
                        urgence={contrat.urgence}
                        en_reconduction={contrat.en_reconduction}
                        reconduction_tacite={contrat.reconduction_tacite}
                      />
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
